import { parseElectronicSensorStreamKey, parseMotorStreamSensorKey } from '../../utils/electronicsDashboardUtils';
import { FrameworkClasses } from '../../framework/frameworkClasses';
import type { FrameworkClass, FrameworkStreamData } from '../../framework';
import { sensorClassNameFromStreamKey } from './sensor-utils';
export type StreamDataBounds = { min?: number; max?: number };

function unionStreamDataBounds(bounds: readonly StreamDataBounds[]): { min: number; max: number } | undefined {
  let min: number | undefined;
  let max: number | undefined;

  for (const bound of bounds) {
    if (typeof bound.min === 'number') {
      min = min === undefined ? bound.min : Math.min(min, bound.min);
    }
    if (typeof bound.max === 'number') {
      max = max === undefined ? bound.max : Math.max(max, bound.max);
    }
  }

  if (typeof min === 'number' && typeof max === 'number') return { min, max };
  return undefined;
}

type BoundsSignature = `unbounded:${string}` | `bounded:${number}:${number}`;

function hasBoundedFrameworkBounds(bounds: StreamDataBounds): boolean {
  return typeof bounds.min === 'number' && typeof bounds.max === 'number';
}

/** Bounded metrics share an axis; each unbounded metric gets its own axis so flat streams still scale independently. */
function boundsSignature(bounds: StreamDataBounds, dataKey: string): BoundsSignature {
  const { min, max } = bounds;
  if (typeof min === 'number' && typeof max === 'number') {
    return `bounded:${min}:${max}`;
  }
  return `unbounded:${dataKey}`;
}

function boundsFromStreamDataEntry(entry: FrameworkStreamData | undefined): StreamDataBounds {
  if (!entry) return {};
  return { min: entry.min, max: entry.max };
}

function frameworkStreamDataEntryForMetric(
  frameworkClass: FrameworkClass | undefined,
  metric: string,
  streamPrefix: string
): FrameworkStreamData | undefined {
  return frameworkClass?.streamData?.find((entry) => {
    const parts = entry.name.split('.');
    return parts[0] === streamPrefix && parts[parts.length - 1] === metric;
  });
}

function findStreamDataEntryByName(dataKey: string): FrameworkStreamData | undefined {
  for (const frameworkClass of FrameworkClasses) {
    const entry = frameworkClass.streamData?.find((item) => item.name === dataKey);
    if (entry) return entry;
  }
  return undefined;
}

function streamDataBoundsForPortStreamKey(parsed: { prefix: string; port: number; metric: string }, className: string | undefined): StreamDataBounds {
  if (!className) return {};

  const frameworkClass = FrameworkClasses.find((fc) => fc.name === className);
  const entry = frameworkStreamDataEntryForMetric(frameworkClass, parsed.metric, parsed.prefix);
  return boundsFromStreamDataEntry(entry);
}

export function streamDataBoundsForSeriesKey(
  dataKey: string,
  motorClassNameByPort?: ReadonlyMap<number, string>,
  mechanismClassNameByPort?: ReadonlyMap<number, string>
): StreamDataBounds {
  const motorParsed = parseMotorStreamSensorKey(dataKey);
  if (motorParsed) {
    const className =
      motorParsed.prefix === 'mechanism' ? mechanismClassNameByPort?.get(motorParsed.port) : motorClassNameByPort?.get(motorParsed.port);
    return streamDataBoundsForPortStreamKey(motorParsed, className);
  }

  const sensorParsed = parseElectronicSensorStreamKey(dataKey);
  if (sensorParsed) {
    return streamDataBoundsForPortStreamKey(sensorParsed, sensorClassNameFromStreamKey(dataKey) ?? undefined);
  }

  return boundsFromStreamDataEntry(findStreamDataEntryByName(dataKey));
}

export type LineGraphYAxisConfig = {
  id: string;
  scaleType: 'linear';
  position?: 'left' | 'right';
  min?: number;
  max?: number;
  domainLimit?: 'strict';
  width?: number;
  tickInterval?: number[];
  tickNumber?: number;
  tickLabelInterval?: () => boolean;
  tickLabelMinGap?: number;
  valueFormatter?: (value: number | null) => string;
  tickLabelStyle?: { fill?: string };
  slotProps?: {
    axisTick?: { stroke?: string };
    axisLine?: { stroke?: string };
  };
};

/** First selected-series color per y-axis id (one axis may serve multiple series with the same bounds). */
export function buildRepresentativeAxisColors(
  series: ReadonlyArray<{ dataKey: string; color?: string }>,
  yAxisIdByDataKey: ReadonlyMap<string, string>
): Map<string, string> {
  const axisColor = new Map<string, string>();
  for (const entry of series) {
    const axisId = yAxisIdByDataKey.get(entry.dataKey);
    if (!axisId || !entry.color || axisColor.has(axisId)) continue;
    axisColor.set(axisId, entry.color);
  }
  return axisColor;
}

export function applySeriesColorToYAxisConfig(axis: LineGraphYAxisConfig, color: string | undefined): LineGraphYAxisConfig {
  if (!color) return axis;
  return {
    ...axis,
    tickLabelStyle: { ...axis.tickLabelStyle, fill: color },
    slotProps: {
      axisTick: { stroke: color },
      axisLine: { stroke: color },
    },
  };
}

type AxisClassNames = {
  id: string;
  line: string;
  tick: string;
  tickLabel: string;
};

/** Targets each y-axis by MUI's per-axis id class (`.MuiChartsAxis-id-${axisId}`). */
export function buildColoredMultiAxisChartSx(
  axisColors: ReadonlyMap<string, string>,
  axisClasses: AxisClassNames
): Record<string, { stroke?: string; fill?: string }> {
  const sx: Record<string, { stroke?: string; fill?: string }> = {};
  for (const [axisId, color] of axisColors) {
    const root = `& .${axisClasses.id}-${axisId}`;
    sx[`${root} .${axisClasses.line}`] = { stroke: color };
    sx[`${root} .${axisClasses.tick}`] = { stroke: color };
    sx[`${root} .${axisClasses.tickLabel}`] = { fill: color };
  }
  return sx;
}

export type LineGraphMultiAxisLayout = {
  useMultiAxis: boolean;
  yAxisById: Map<string, LineGraphYAxisConfig>;
  yAxisIdByDataKey: Map<string, string>;
};

export const EMPTY_LINE_GRAPH_MULTI_AXIS_LAYOUT: LineGraphMultiAxisLayout = {
  useMultiAxis: false,
  yAxisById: new Map(),
  yAxisIdByDataKey: new Map(),
};

/** Multi-axis is only for `motor_multi` widgets (multiple metrics on one port), not multi-port motor charts. */
export function shouldUseMotorMultiAxisLayout(useMotorMetricSeriesLabels?: boolean): boolean {
  return useMotorMetricSeriesLabels === true;
}

export type BuildLineGraphMultiAxisLayoutOptions = {
  seriesDataKeys: readonly string[];
  motorClassNameByPort?: ReadonlyMap<number, string>;
  mechanismClassNameByPort?: ReadonlyMap<number, string>;
  estimateYAxisWidth: (tickValues: readonly number[]) => number;
  buildTickValues: (min: number, max: number) => number[];
  formatTickLabel: (value: number | null) => string;
};

/**
 * When selected series use incompatible framework bounds, assigns each bounds group its own y-axis.
 * Two groups use left/right placement; three or more stack additional axes on the left.
 */
export function buildLineGraphMultiAxisLayout({
  seriesDataKeys,
  motorClassNameByPort,
  mechanismClassNameByPort,
  estimateYAxisWidth,
  buildTickValues,
  formatTickLabel,
}: BuildLineGraphMultiAxisLayoutOptions): LineGraphMultiAxisLayout {
  const yAxisById = new Map<string, LineGraphYAxisConfig>();
  const yAxisIdByDataKey = new Map<string, string>();
  const signatureToAxisId = new Map<BoundsSignature, string>();

  for (const dataKey of seriesDataKeys) {
    const bounds = streamDataBoundsForSeriesKey(dataKey, motorClassNameByPort, mechanismClassNameByPort);
    const signature = boundsSignature(bounds, dataKey);
    let axisId = signatureToAxisId.get(signature);
    if (!axisId) {
      axisId = `y-axis-${signatureToAxisId.size}`;
      signatureToAxisId.set(signature, axisId);

      if (!hasBoundedFrameworkBounds(bounds)) {
        yAxisById.set(axisId, { id: axisId, scaleType: 'linear', valueFormatter: formatTickLabel });
      } else {
        const resolved = unionStreamDataBounds([bounds]);
        if (!resolved) {
          yAxisById.set(axisId, { id: axisId, scaleType: 'linear', valueFormatter: formatTickLabel });
        } else {
          const tickInterval = buildTickValues(resolved.min, resolved.max);
          yAxisById.set(axisId, {
            id: axisId,
            scaleType: 'linear',
            min: resolved.min,
            max: resolved.max,
            domainLimit: 'strict',
            width: estimateYAxisWidth(tickInterval),
            tickInterval,
            tickNumber: tickInterval.length,
            tickLabelInterval: () => true,
            tickLabelMinGap: 0,
            valueFormatter: formatTickLabel,
          });
        }
      }
    }
    yAxisIdByDataKey.set(dataKey, axisId);
  }

  const axisIds = [...signatureToAxisId.values()];
  const useMultiAxis = axisIds.length > 1;

  if (useMultiAxis) {
    axisIds.forEach((axisId, index) => {
      const config = yAxisById.get(axisId);
      if (!config) return;
      if (index === 0) config.position = 'left';
      else if (index === 1) config.position = 'right';
      else config.position = 'left';
    });
  }

  return {
    useMultiAxis,
    yAxisById,
    yAxisIdByDataKey,
  };
}
